import axios from "axios";

/**
 * Resolve the correct backend base URL.
 *
 * Priority:
 *   1. If REACT_APP_BACKEND_URL is set AND its host matches the browser host,
 *      use it. (Explicit config path.)
 *   2. Otherwise, use the current window origin. In this deployment the
 *      frontend and backend are served behind the same ingress: any request
 *      to `/api/*` is routed to the FastAPI backend by Kubernetes, so
 *      same-origin is always correct.
 *
 * This makes the app immune to stale/misconfigured .env values and to
 * being accessed from a different preview URL than the one baked in at
 * build time.
 */
function resolveBackendBase() {
  const configured = process.env.REACT_APP_BACKEND_URL;
  if (typeof window === "undefined") return configured || "";
  try {
    if (configured) {
      const c = new URL(configured);
      if (c.host === window.location.host) return configured.replace(/\/$/, "");
    }
  } catch {
    // fall through
  }
  return window.location.origin;
}

export const BACKEND_URL = resolveBackendBase();
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// A bare axios instance for refresh (avoid interceptor recursion).
const bareAxios = axios.create({ baseURL: API, withCredentials: true });

let refreshPromise = null;
function attemptRefresh() {
  if (!refreshPromise) {
    refreshPromise = bareAxios
      .post("/auth/refresh")
      .finally(() => {
        // release the lock a tick later so concurrent callers piggyback
        setTimeout(() => { refreshPromise = null; }, 0);
      });
  }
  return refreshPromise;
}

/***
 * API request interceptor: automatically attach the user token if present.   
 */
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("user_token");
    console.info('token :', token);
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Global 401 handler:
 *   - Skip for /auth/login and /auth/refresh (no refresh loop).
 *   - Try /auth/refresh once. If it succeeds, replay the original request.
 *   - If refresh fails and we're not already on /login, redirect to /login.
 */
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error.response?.status;
    const cfg = error.config || {};
    const url = cfg.url || "";
    if (
      status === 401 &&
      !cfg._retried &&
      !url.includes("/auth/login") &&
      !url.includes("/auth/refresh") &&
      !url.includes("/auth/logout")
    ) {
      cfg._retried = true;
      try {
        await attemptRefresh();
        return api(cfg);
      } catch {
        // fall through to redirect
      }
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.replace("/login");
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/** Convert an Axios error into a human string. Falls back to generic message. */
export function formatAxiosError(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;
  const detail = err.response?.data?.detail;
  const fromDetail = formatApiErrorDetail(detail);
  if (fromDetail) return fromDetail;
  if (err.response) {
    return `Request failed (HTTP ${err.response.status}). ${fallback}`;
  }
  if (err.request) {
    return "Cannot reach the server. Please check your connection and try again.";
  }
  return err.message || fallback;
}

export default api;
